import { DownOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Checkbox, ConfigProvider, DatePicker, Drawer, Dropdown, Flex, Layout, Modal, Pagination, Table, notification } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ColumnDropdown } from "../../components/common/ColumnDropdown";
import { LoaderSpinner } from "../../components/common/Loader";
import { downloadBusPassMailMerge, exportBusPassRecords, getBusPassRequest, getMemberPassTypeFiles, getPassFormatRules } from "../../service/httpsCalls";
import { BusPassAssignmentColumnsConfig, createTableConfig } from "../Billing/tableConfig";
import BusPassReportForm from "../Reports/components/BusPassReportForm";
import "./busPassAssignment.scss";

const { RangePicker } = DatePicker;
const BUS_PASS_ROW_KEY = "__busPassAssignmentRowKey";

interface CheckedProps {
    item: string;
    checked: boolean;
}

interface PaginationState {
    currentPage: number;
    pageSize: number;
    totalRecords: number;
}

interface BusPassHistoryItem {
    MemberId?: string;
    FileTypeName?: string;
    FileName?: string;
    EffectiveStart?: string;
    EffectiveEnd?: string;
    Status?: string;
    CreatedDate?: string;
    CreatedBy?: string;
}

type NotificationType = "success" | "info" | "warning" | "error";

interface ResizableHeaderCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
    width?: number;
    onColumnResize?: (width: number) => void;
}

const ResizableHeaderCell = ({ width, onColumnResize, children, style, ...restProps }: ResizableHeaderCellProps) => {
    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!width || !onColumnResize) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const startX = event.clientX;
        const startWidth = width;
        const originalCursor = document.body.style.cursor;
        const originalUserSelect = document.body.style.userSelect;

        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";

        const handleMouseMove = (moveEvent: MouseEvent) => {
            onColumnResize(Math.max(80, startWidth + moveEvent.clientX - startX));
        };

        const handleMouseUp = () => {
            document.body.style.cursor = originalCursor;
            document.body.style.userSelect = originalUserSelect;
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
    };

    return (
        <th
            {...restProps}
            style={{
                ...style,
                position: "relative",
                width
            }}
        >
            {children}
            {onColumnResize && (
                <div
                    onMouseDown={handleMouseDown}
                    style={{
                        bottom: 0,
                        cursor: "col-resize",
                        position: "absolute",
                        right: -3,
                        top: 0,
                        width: 8,
                        zIndex: 1
                    }}
                />
            )}
        </th>
    );
};

const getResponseItems = (response: any) => {
    if (Array.isArray(response)) {
        return response;
    }

    if (Array.isArray(response?.items)) {
        return response.items;
    }

    if (Array.isArray(response?.Items)) {
        return response.Items;
    }

    return [];
};

const formatHistoryDate = (value?: string, includeTime = false) => {
    if (!value) {
        return "-";
    }

    const parsedDate = dayjs(value);
    if (!parsedDate.isValid()) {
        return value;
    }

    return parsedDate.format(includeTime ? "MM/DD/YYYY hh:mm A" : "MM/DD/YYYY");
};

const renderRuleCell = (value: any) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }

    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }

    const displayValue = typeof value === "object" ? JSON.stringify(value) : String(value);

    return (
        <span title={displayValue} style={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {displayValue}
        </span>
    );
};

const isClientCreatedTrip = (record: any) => {
    const value = record?.IsClientCreatedTrip ?? record?.isClientCreatedTrip;

    if (typeof value === "boolean") {
        return value;
    }

    if (typeof value === "number") {
        return value === 1;
    }

    if (typeof value === "string") {
        const normalizedValue = value.trim().toLowerCase();
        return normalizedValue === "true" || normalizedValue === "1";
    }

    return false;
};

const isBscClient = (record: any) => {
    const clientName = String(record?.ClientName ?? record?.clientName ?? "").toLowerCase();
    const clientId = String(record?.ClientId ?? record?.clientId ?? "");

    return clientId === "13" || clientName.includes("bsc") || clientName.includes("blue shield");
};

const getHealthPlanName = (record: any) => {
    const clientName = String(record?.ClientName ?? record?.clientName ?? "").trim();

    if (clientName) {
        return clientName;
    }

    const clientId = String(record?.ClientId ?? record?.clientId ?? "");
    if (clientId === "13") {
        return "Blue Shield of California";
    }

    if (clientId === "16") {
        return "Inland Empire Health Plan";
    }

    return "selected health plan";
};

const formatHealthPlanList = (healthPlans: string[]) => {
    const uniqueHealthPlans = Array.from(new Set(healthPlans.filter(Boolean)));

    if (uniqueHealthPlans.length === 0) {
        return "selected health plan";
    }

    if (uniqueHealthPlans.length === 1) {
        return uniqueHealthPlans[0];
    }

    return `${uniqueHealthPlans.slice(0, -1).join(", ")} and ${uniqueHealthPlans[uniqueHealthPlans.length - 1]}`;
};

const passFormatRulesColumns = [
    {
        title: "Transit Agency",
        dataIndex: "TransitAgencyName",
        width: 210,
        render: renderRuleCell
    },
    {
        title: "Pass Type",
        dataIndex: "PassType",
        width: 150,
        render: renderRuleCell
    },
    {
        title: "Reservation Electronic Pref",
        dataIndex: "ReservationElectronicPref",
        width: 220,
        render: (value: any, record: any) => renderRuleCell(value ?? record?.ReservationElectronicPreference)
    },
    {
        title: "Member Electronic Pref",
        dataIndex: "MemberElectronicPref",
        width: 205,
        render: (value: any, record: any) => renderRuleCell(value ?? record?.MemberElectronicPreference)
    },
    {
        title: "Pass Format",
        dataIndex: "PassFormat",
        width: 150,
        render: (value: any, record: any) => renderRuleCell(value ?? record?.PassFormatName ?? record?.FileTypeName)
    }
];

export default function BusPassAssignment() {
    const [api, contextHolder] = notification.useNotification();
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [mailMergeLoading, setMailMergeLoading] = useState(false);
    const [showTable, setShowTable] = useState(false);
    const [reportData, setReportData] = useState<any[]>([]);
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [tableHeight, setTableHeight] = useState(300);
    const [menuVisible, setMenuVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<any | null>(null);
    const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyRows, setHistoryRows] = useState<BusPassHistoryItem[]>([]);
    const [historyRecord, setHistoryRecord] = useState<any | null>(null);
    const [rulesDrawerOpen, setRulesDrawerOpen] = useState(false);
    const [rulesLoading, setRulesLoading] = useState(false);
    const [rulesRows, setRulesRows] = useState<any[] | null>(null);
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
    const [excludedRowKeys, setExcludedRowKeys] = useState<React.Key[]>([]);
    const tableContainerRef = useRef<HTMLDivElement | null>(null);
    const fetchRequestIdRef = useRef(0);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [pagination, setPagination] = useState<PaginationState>({
        currentPage: 1,
        pageSize: 10,
        totalRecords: 0
    });

    const openNotificationWithIcon = (type: NotificationType, message: string, description = "") => {
        api[type]({
            message,
            description
        });
    };

    const openHistoryDrawer = useCallback((record: any) => {
        const memberId = record?.MbrID ?? record?.MemberId ?? record?.memberId;

        if (!memberId) {
            openNotificationWithIcon("warning", "Member ID is required to load history.");
            return;
        }

        setHistoryRecord(record);
        setHistoryRows([]);
        setHistoryDrawerOpen(true);
        setHistoryLoading(true);

        getMemberPassTypeFiles({ MemberId: memberId }, (res: any) => {
            setHistoryLoading(false);

            if (res?.hasError) {
                const errorMsg =
                    res?.error?.messages?.[0]?.description ??
                    res?.error?.message ??
                    res?.message ??
                    "Unable to load bus pass history.";
                openNotificationWithIcon("error", errorMsg);
                return;
            }

            setHistoryRows(getResponseItems(res));
        });
    }, []);

    const openRulesDrawer = useCallback(() => {
        setRulesDrawerOpen(true);

        if (rulesRows !== null) {
            return;
        }

        setRulesLoading(true);
        getPassFormatRules((res: any) => {
            setRulesLoading(false);

            if (res?.hasError) {
                const errorMsg =
                    res?.error?.messages?.[0]?.description ??
                    res?.error?.message ??
                    res?.message ??
                    "Unable to load pass format rules.";
                openNotificationWithIcon("error", errorMsg);
                return;
            }

            setRulesRows(getResponseItems(res));
        });
    }, [rulesRows]);

    const handleModalOpen = useCallback((componentName: string, record: any) => {
        if (componentName === "BusPassReportForm") {
            setSelectedRecord(record);
            return;
        }

        if (componentName === "BusPassHistory") {
            openHistoryDrawer(record);
        }
    }, [openHistoryDrawer]);

    const baseColumns = useMemo(() => createTableConfig(BusPassAssignmentColumnsConfig, handleModalOpen), [handleModalOpen]);
    const [isChecked, setChecked] = useState<CheckedProps[]>(
        BusPassAssignmentColumnsConfig.map((entry: any) => ({ item: entry.title, checked: !entry.hidden }))
    );

    const columns = useMemo(() => baseColumns.map((item: any, index: number) => {
        const columnKey = String(item.dataIndex || item.key || item.title || index);
        const width = columnWidths[columnKey] ?? item.width;

        return {
            ...item,
            width,
            hidden: !isChecked.find(x => x.item === item.title)?.checked,
            onHeaderCell: (column: any) => ({
                ...(typeof item.onHeaderCell === "function" ? item.onHeaderCell(column) : {}),
                width,
                onColumnResize: (nextWidth: number) => {
                    setColumnWidths(prev => ({
                        ...prev,
                        [columnKey]: nextWidth
                    }));
                }
            })
        };
    }), [baseColumns, columnWidths, isChecked]);

    const visibleColumns = useMemo(() => columns.filter((column: any) => !column.hidden), [columns]);
    const scrollX = useMemo(
        () => 110 + visibleColumns.reduce((acc: number, col: any) => acc + (col.width || 150), 0),
        [visibleColumns]
    );
    const tableComponents = useMemo(() => ({
        header: {
            cell: ResizableHeaderCell
        }
    }), []);
    const historyColumns = useMemo(() => [
        {
            title: "File Type",
            dataIndex: "FileTypeName",
            width: 210
        },
        {
            title: "File Name",
            dataIndex: "FileName",
            width: 280,
            render: (text: string) => <span style={{ overflowWrap: "anywhere" }}>{text || "-"}</span>
        },
        {
            title: "Effective Start",
            dataIndex: "EffectiveStart",
            width: 140,
            render: (value: string) => formatHistoryDate(value)
        },
        {
            title: "Effective End",
            dataIndex: "EffectiveEnd",
            width: 140,
            render: (value: string) => formatHistoryDate(value)
        },
        {
            title: "Status",
            dataIndex: "Status",
            width: 150
        },
        {
            title: "Created Date",
            dataIndex: "CreatedDate",
            width: 170,
            render: (value: string) => formatHistoryDate(value, true)
        },
        {
            title: "Created By",
            dataIndex: "CreatedBy",
            width: 170
        }
    ], []);
    const rulesScrollX = useMemo(
        () => Math.max(900, passFormatRulesColumns.reduce((acc: number, column: any) => acc + (column.width || 150), 0)),
        []
    );
    const isDateRangeSelected = !!dateRange[0] && !!dateRange[1];
    const getBusPassRowKey = useCallback((record: any, index?: number) =>
        record?.SequenceNumber ?? record?.sequenceNumber ?? record?.[BUS_PASS_ROW_KEY] ?? `${record?.MbrID ?? "member"}-${record?.ReservationID ?? "reservation"}-${record?.TripLegIDs ?? record?.TripIDs ?? index}`,
    []);
    const allRowKeys = useMemo(() => reportData.map((row, index) => getBusPassRowKey(row, index)), [getBusPassRowKey, reportData]);
    const allRowsSelected = reportData.length > 0 && excludedRowKeys.length === reportData.length;
    const partiallySelected = excludedRowKeys.length > 0 && !allRowsSelected;
    const toggleAllExcludedRows = useCallback(() => {
        setExcludedRowKeys(allRowsSelected ? [] : allRowKeys);
    }, [allRowKeys, allRowsSelected]);
    const rowSelection = useMemo(() => ({
        columnTitle: (
            <span style={{ alignItems: "center", display: "inline-flex", gap: 8 }}>
                <Checkbox
                    checked={allRowsSelected}
                    indeterminate={partiallySelected}
                    onChange={toggleAllExcludedRows}
                />
                <span>Exclude</span>
            </span>
        ),
        columnWidth: 110,
        preserveSelectedRowKeys: true,
        selectedRowKeys: excludedRowKeys,
        onChange: (selectedRowKeys: React.Key[]) => {
            setExcludedRowKeys(selectedRowKeys);
        }
    }), [allRowsSelected, excludedRowKeys, partiallySelected, toggleAllExcludedRows]);
    const pagedReportData = useMemo(() => {
        const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
        return reportData.slice(startIndex, startIndex + pagination.pageSize);
    }, [pagination.currentPage, pagination.pageSize, reportData]);
    const excludedRows = useMemo(() =>
        excludedRowKeys
            .filter(sequenceNumber => sequenceNumber !== null && sequenceNumber !== undefined && sequenceNumber !== "")
            .join(","),
    [excludedRowKeys]);
    const excludedRowKeySet = useMemo(() => new Set(excludedRowKeys), [excludedRowKeys]);
    const getRowsIncludedForMailMerge = useCallback((templateType: "Letter" | "CoverLetter") =>
        reportData.filter((row, index) => {
            if (excludedRowKeySet.has(getBusPassRowKey(row, index))) {
                return false;
            }

            return templateType !== "CoverLetter" || !isBscClient(row);
        }),
    [excludedRowKeySet, getBusPassRowKey, reportData]);
    const getMailMergeExcludedRows = useCallback((templateType: "Letter" | "CoverLetter") => {
        const excludedKeys = new Set(excludedRowKeys);

        if (templateType === "CoverLetter") {
            reportData.forEach((row, index) => {
                if (isBscClient(row)) {
                    excludedKeys.add(getBusPassRowKey(row, index));
                }
            });
        }

        return Array.from(excludedKeys)
            .filter(sequenceNumber => sequenceNumber !== null && sequenceNumber !== undefined && sequenceNumber !== "")
            .join(",");
    }, [excludedRowKeys, getBusPassRowKey, reportData]);
    const getMailMergeDownloadMessage = useCallback((templateType: "Letter" | "CoverLetter") => {
        const includedRows = getRowsIncludedForMailMerge(templateType);
        const healthPlans = formatHealthPlanList(includedRows.map(getHealthPlanName));

        return templateType === "Letter"
            ? `Downloaded letters for ${healthPlans} members.`
            : `Downloaded envelopes for ${healthPlans} members.`;
    }, [getRowsIncludedForMailMerge]);

    const updateTableHeight = useCallback(() => {
        const container = tableContainerRef.current;
        if (!container) {
            return false;
        }

        const header = container.querySelector(".ant-table-header") as HTMLElement | null;
        const paginationElement = container.querySelector(".ant-pagination") as HTMLElement | null;
        const containerHeight = container.clientHeight || container.getBoundingClientRect().height;
        const headerHeight = header?.offsetHeight || header?.getBoundingClientRect().height || 0;
        const paginationHeight = paginationElement?.offsetHeight || paginationElement?.getBoundingClientRect().height || 40;

        if (!containerHeight || !headerHeight) {
            return false;
        }

        const nextHeight = Math.floor(containerHeight - headerHeight - paginationHeight - 20);
        const normalizedHeight = Math.max(nextHeight, 220);

        setTableHeight((prev) => Math.abs(prev - normalizedHeight) > 1 ? normalizedHeight : prev);
        return true;
    }, []);

    const fetchData = useCallback(() => {
        if (!dateRange[0] || !dateRange[1]) {
            openNotificationWithIcon("warning", "Date range is required.");
            return;
        }

        const requestId = fetchRequestIdRef.current + 1;
        fetchRequestIdRef.current = requestId;

        setShowTable(true);
        setReportData([]);
        setExcludedRowKeys([]);
        setPagination(prev => ({ ...prev, currentPage: 1, totalRecords: 0 }));
        setLoading(true);
        getBusPassRequest({
            startDate: dateRange[0] ? dateRange[0].startOf("day").format("YYYY-MM-DD") : null,
            endDate: dateRange[1] ? dateRange[1].startOf("day").format("YYYY-MM-DD") : null,
            pageNumber: null,
            pageSize: null
        }, (res: any) => {
            if (requestId !== fetchRequestIdRef.current) {
                return;
            }

            setLoading(false);

            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : "Unable to load bus pass assignment data.";
                openNotificationWithIcon("error", errorMsg);
                setReportData([]);
                setExcludedRowKeys([]);
                setPagination(prev => ({ ...prev, currentPage: 1, totalRecords: 0 }));
                return;
            }

            const items = getResponseItems(res).map((item: any, index: number) => ({
                ...item,
                [BUS_PASS_ROW_KEY]: `${item?.SequenceNumber ?? item?.sequenceNumber ?? "sequence"}-${item?.MbrID ?? "member"}-${item?.ReservationID ?? "reservation"}-${item?.TripLegIDs ?? item?.TripIDs ?? "trip"}-${index}`
            }));
            setReportData(items);
            setPagination(prev => ({
                ...prev,
                currentPage: 1,
                totalRecords: items.length
            }));

            if (!items.length) {
                openNotificationWithIcon("info", "No data to display.");
            }
        });
    }, [dateRange]);

    const search = () => {
        fetchData();
    };

    const resetFilters = () => {
        fetchRequestIdRef.current += 1;
        setLoading(false);
        setDateRange([null, null]);
        setReportData([]);
        setExcludedRowKeys([]);
        setShowTable(false);
        setPagination(prev => ({
            ...prev,
            currentPage: 1,
            totalRecords: 0
        }));
    };

    const handlePaginationChange = (page: number, pageSize: number) => {
        setPagination(prev => ({
            ...prev,
            currentPage: page,
            pageSize,
            totalRecords: reportData.length
        }));
    };

    const exportRecords = () => {
        if (!dateRange[0] || !dateRange[1]) {
            openNotificationWithIcon("warning", "Date range is required.");
            return;
        }

        setExportLoading(true);
        exportBusPassRecords({
            startDate: dateRange[0] ? dateRange[0].startOf("day").format("YYYY-MM-DD") : null,
            endDate: dateRange[1] ? dateRange[1].startOf("day").format("YYYY-MM-DD") : null,
            ExcludedRows: excludedRows
        }, (res: any) => {
            setExportLoading(false);
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : "Unable to export bus pass records.";
                openNotificationWithIcon("error", errorMsg);
                return;
            }

            if (res?.message) {
                openNotificationWithIcon("info", res.message);
                return;
            }

            if (res?.file) {
                downloadFile(res.file, res.fileName ?? "BusPassGenerationReport.xlsx");
                openNotificationWithIcon("success", "Bus pass records downloaded.");
                return;
            }

            openNotificationWithIcon("error", "Unable to export bus pass records.");
        });
    };

    const getBusPassDownloadRequest = (templateType: "Letter" | "CoverLetter") => ({
        startDate: dateRange[0] ? dateRange[0].startOf("day").format("YYYY-MM-DD") : null,
        endDate: dateRange[1] ? dateRange[1].startOf("day").format("YYYY-MM-DD") : null,
        ExcludedRows: templateType === "CoverLetter" ? getMailMergeExcludedRows(templateType) : excludedRows
    });

    const downloadFile = (file: Blob, fileName: string) => {
        const url = window.URL.createObjectURL(file);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const downloadMailMerge = (templateType: "Letter" | "CoverLetter") => {
        if (!dateRange[0] || !dateRange[1]) {
            openNotificationWithIcon("warning", "Date range is required.");
            return;
        }

        const includedRows = getRowsIncludedForMailMerge(templateType);
        if (includedRows.length === 0) {
            openNotificationWithIcon("info", "No records found.");
            return;
        }

        setMailMergeLoading(true);
        downloadBusPassMailMerge({
            ...getBusPassDownloadRequest(templateType),
            templateType
        }, (res: any) => {
            setMailMergeLoading(false);

            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : "Unable to download bus pass mail merge.";
                openNotificationWithIcon("error", errorMsg);
                return;
            }

            if (!res?.file) {
                return;
            }

            downloadFile(res.file, res.fileName ?? "BusPassMailMerge.pdf");
            openNotificationWithIcon("success", getMailMergeDownloadMessage(templateType));
        });
    };

    const runExportAction = (key: string) => {
        if (key === "report") {
            exportRecords();
            return;
        }

        if (key === "letter") {
            downloadMailMerge("Letter");
            return;
        }

        if (key === "envelope") {
            downloadMailMerge("CoverLetter");
        }
    };

    const getExportActionLabel = (key: string) => {
        if (key === "report") {
            return "export report";
        }

        if (key === "letter") {
            return "download letters";
        }

        if (key === "envelope") {
            return "download envelopes";
        }

        return "continue";
    };

    const handleExportMenuClick = ({ key }: { key: string }) => {
        const excludedCount = excludedRowKeys.length;
        const totalRecords = reportData.length;
        const includedCount = Math.max(totalRecords - excludedCount, 0);

        if (totalRecords > 0 && excludedCount === totalRecords) {
            Modal.info({
                title: "No records to process",
                content: `All ${totalRecords} records are selected to exclude. Clear at least one excluded row before continuing.`
            });
            return;
        }

        Modal.confirm({
            title: excludedCount > 0 ? "Confirm action with excluded records" : "Confirm action for all records",
            content: excludedCount > 0
                ? `${excludedCount} rows are selected to exclude. This will ${getExportActionLabel(key)} for ${includedCount} of ${totalRecords} records. Do you want to continue?`
                : `No rows are selected to exclude. This will ${getExportActionLabel(key)} for all ${totalRecords} records. Do you want to continue?`,
            okText: "Continue",
            cancelText: "Cancel",
            onOk: () => runExportAction(key)
        });
    };

    const exportMenuItems = [
        { key: "report", label: "Export Report" },
        { key: "letter", label: "Download Letter" },
        { key: "envelope", label: "Download Envelopes" }
    ];

    const handleBusPassReportGenerated = useCallback(() => {
        setSelectedRecord(null);
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        document.title = "Bus Pass Assignment";
    }, []);

    useEffect(() => {
        if (!showTable) {
            return;
        }

        let frameId: number | null = null;
        let resizeObserver: ResizeObserver | null = null;
        let isDisposed = false;

        const scheduleTableHeightUpdate = () => {
            if (isDisposed) {
                return;
            }

            if (frameId) {
                window.cancelAnimationFrame(frameId);
            }

            frameId = window.requestAnimationFrame(() => {
                if (updateTableHeight() || isDisposed) {
                    return;
                }

                frameId = window.requestAnimationFrame(scheduleTableHeightUpdate);
            });
        };

        scheduleTableHeightUpdate();

        if (typeof ResizeObserver !== "undefined" && tableContainerRef.current) {
            resizeObserver = new ResizeObserver(scheduleTableHeightUpdate);
            resizeObserver.observe(tableContainerRef.current);
        }

        window.addEventListener("resize", scheduleTableHeightUpdate);

        return () => {
            isDisposed = true;
            if (frameId) {
                window.cancelAnimationFrame(frameId);
            }
            resizeObserver?.disconnect();
            window.removeEventListener("resize", scheduleTableHeightUpdate);
        };
    }, [showTable, reportData.length, visibleColumns.length, pagination.pageSize, pagination.totalRecords, updateTableHeight]);

    return (
        <ConfigProvider theme={{
            components: {
                Layout: { bodyBg: "#eff1f5", siderBg: "#fff" }
            }
        }}>
            {contextHolder}
            <Layout style={{
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                height: "calc(100vh - 57px)",
                minHeight: 0,
                overflow: "hidden",
                padding: "20px",
                width: "100%"
            }}>
                <h3 style={{ textTransform: "uppercase", marginTop: "0px" }}>Bus Pass Assignment</h3>
                <div style={{ background: "#fff", boxSizing: "border-box", marginBottom: "1rem", padding: "1rem", width: "100%" }}>
                    <Flex align="center" gap="small" style={{ flexWrap: "nowrap", minWidth: 0, width: "100%" }}>
                        <label style={{ alignItems: "center", display: "inline-flex", flex: "0 0 auto" }}><SearchOutlined />&nbsp;Search</label>
                        <RangePicker
                            placeholder={["From Date", "To Date"]}
                            style={{ flex: "0 1 475px", maxWidth: "38vw", minWidth: 360 }}
                            format="MM/DD/YYYY"
                            value={dateRange}
                            onChange={(dates) => {
                                if (Array.isArray(dates) && dates.length) {
                                    setDateRange([dates[0] ?? null, dates[1] ?? null]);
                                } else {
                                    setDateRange([null, null]);
                                }

                                fetchRequestIdRef.current += 1;
                                setLoading(false);
                                setReportData([]);
                                setExcludedRowKeys([]);
                                setShowTable(false);
                                setPagination(prev => ({ ...prev, currentPage: 1, totalRecords: 0 }));
                            }}
                        />
                        <Button onClick={search} loading={loading} type="primary">
                            Search
                        </Button>
                        <Button onClick={resetFilters} style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9", color: "rgba(0, 0, 0, 0.88)" }} type="primary">
                            Reset
                        </Button>
                        <span style={{ flex: 1, minWidth: 16 }} />
                        {reportData.length > 0 && (
                            <>
                                <Dropdown
                                    menu={{
                                        items: exportMenuItems,
                                        onClick: handleExportMenuClick
                                    }}
                                    disabled={!isDateRangeSelected || exportLoading || mailMergeLoading}
                                >
                                    <Button loading={exportLoading || mailMergeLoading} type="primary">
                                        Export <DownOutlined />
                                    </Button>
                                </Dropdown>
                                <Button onClick={openRulesDrawer} loading={rulesLoading} type="primary">
                                    Rules
                                </Button>
                            </>
                        )}
                    </Flex>
                </div>

                <ColumnDropdown columns={baseColumns} menuVisible={menuVisible} position={position} isChecked={isChecked} setChecked={setChecked} />

                {showTable &&
                        <div
                            ref={tableContainerRef}
                            id="bus-pass-assignment-table-container"
                            style={{ background: "#fff", display: "flex", flex: 1, flexDirection: "column", minHeight: 0, overflow: "hidden", width: "100%" }}
                        >
                            <Table
                                components={tableComponents}
                                style={{ background: "#fff", flex: 1, minHeight: 0, width: "100%" }}
                                scroll={{ y: tableHeight, x: scrollX }}
                                dataSource={pagedReportData}
                                pagination={false}
                                columns={columns}
                                rowKey={getBusPassRowKey}
                                rowClassName={(record) => isClientCreatedTrip(record) ? "bus-pass-client-created-row" : ""}
                                rowSelection={rowSelection}
                                size="small"
                                loading={{ indicator: <LoaderSpinner size={24} topMargin="10px" />, spinning: loading }}
                                onHeaderRow={() => ({
                                    onContextMenu: (event) => {
                                        event.preventDefault();
                                        if (!menuVisible) {
                                            document.addEventListener("click", function onClickOutside() {
                                                setMenuVisible(false);
                                                document.removeEventListener("click", onClickOutside);
                                            });
                                        }
                                        setMenuVisible(true);
                                        setPosition({ x: event.clientX, y: event.clientY });
                                    }
                                })}
                            />
                            <Pagination
                                current={pagination.currentPage}
                                pageSize={pagination.pageSize}
                                total={reportData.length}
                                showTotal={(total, range) => `${range[0] || 0}-${range[1] || 0} of ${total} items`}
                                showSizeChanger
                                onChange={handlePaginationChange}
                                style={{ display: "flex", justifyContent: "flex-end", padding: "12px 0 0" }}
                            />
                        </div>
                }

                <Drawer
                    title="Bus Pass Process"
                    open={!!selectedRecord}
                    onClose={() => setSelectedRecord(null)}
                    destroyOnClose
                    maskClosable={false}
                    width={720}
                >
                    <BusPassReportForm
                        initialValues={selectedRecord}
                        onReportGenerated={handleBusPassReportGenerated}
                    />
                </Drawer>

                <Drawer
                    title="Bus Pass History"
                    placement="right"
                    open={historyDrawerOpen}
                    onClose={() => {
                        setHistoryDrawerOpen(false);
                        setHistoryRows([]);
                        setHistoryRecord(null);
                    }}
                    destroyOnClose
                    width={980}
                >
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ color: "rgba(0, 0, 0, 0.45)", fontSize: 12, marginBottom: 4 }}>
                            Member
                        </div>
                        <div style={{ fontWeight: 500 }}>
                            {historyRecord?.MbrName && historyRecord?.MbrID
                                ? `${historyRecord.MbrName} - ${historyRecord.MbrID}`
                                : (historyRecord?.MbrName ?? historyRecord?.MbrID ?? "-")}
                        </div>
                    </div>

                    <Table
                        columns={historyColumns}
                        dataSource={historyRows}
                        loading={historyLoading}
                        pagination={{ pageSize: 10, showSizeChanger: false }}
                        rowKey={(record, index) => `${record?.FileName ?? "file"}-${record?.CreatedDate ?? index}`}
                        scroll={{ x: 1260, y: "calc(100vh - 270px)" }}
                        size="small"
                    />
                </Drawer>

                <Drawer
                    title="Pass Format Rules"
                    placement="right"
                    open={rulesDrawerOpen}
                    onClose={() => setRulesDrawerOpen(false)}
                    width={1000}
                >
                    <Table
                        columns={passFormatRulesColumns}
                        dataSource={rulesRows ?? []}
                        loading={rulesLoading}
                        pagination={false}
                        rowKey={(record, index) => `${record?.Id ?? record?.ID ?? record?.RuleId ?? "rule"}-${index}`}
                        scroll={{ x: rulesScrollX, y: "calc(100vh - 210px)" }}
                        size="small"
                    />
                </Drawer>
            </Layout>
        </ConfigProvider>
    );
}
